import getConfig from 'next/config';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { constants } from '../../config/constants';
import { withApplicationContext } from '../../utils/ApplicationContext';
import { withLabels } from '../../utils/LabelsContext';
import { noop } from '../../utils/noop';
import {
  navigateWithUrl,
  navigateWithUrlForBrand,
  navigateWithUrlForCategory,
  navigateWithUrlForCollection,
  navigateWithUrlForSeller,
  navigateWithUrlForShop,
} from '../../utils/RouteHandler';
import MatchMedia from '../ui/MatchMedia/MatchMedia';
import FacetsDesktop from './Desktop/FacetsDesktop';
import { facetFilters } from './FacetEvents';
import DynamicFacets from './Mobile/DynamicFacets';
import FacetsMobile from './Mobile/FacetsMobile';
import { getfacetsWithUpdatedBrandValues } from './VerMasFacet/VerMasFacetHelper';

const { publicRuntimeConfig } = getConfig();

const Facets = (props) => {
  const {
    baseFacetsURL,
    appCtx,
    sortOptions,
    data: initialData,
    availabilityFacets,
    labels,
    pageName,
    onFacetChange,
    handleScrollToTopProductList,
    appliedFacets,
    pagination,
    canonicalUrl,
  } = props;
  const hideMenu = useRef();
  const [data, setData] = useState(initialData);
  const [isFiltersMenuVisible, setIsFiltersMenuVisible] = useState(false);
  const { tenant, setBusy, store, regionCode, siteConfig } = appCtx;
  const dropDownLabel = labels.ORDER_BY_MOBILE;
  const {
    isFacetWithSwitchVisible = false,
    isEventToggleVisible: isEventSwitchVisible = false,
    isDynamicFacetsEnabled = false,
    isWhiteLabelExperience = false,
    isNewOmniChannelExperience = true,
  } = siteConfig?.toggles || {};

  const nonPksAvlFacets = availabilityFacets.filter(({ facetType }) => !/.pks_/.test(facetType));
  const showFreeShippingSwitch =
    isFacetWithSwitchVisible && !(isNewOmniChannelExperience && nonPksAvlFacets.length > 0);
  const showShippingFacetsBar = isNewOmniChannelExperience && nonPksAvlFacets.length > 0;

  const freeShippingFacet = isNewOmniChannelExperience
    ? data.find((d) => constants.FREE_SHIPPING_REGEX.test(d.name))
    : {};

  const { query = {} } = useRouter();
  const { id = '', name = '', isPLP = '', Ntt = '' } = query;
  const xlpId = pageName === 'search' ? Ntt : id;

  const selectedURLs = {};

  const getUpdatedFacets = async (facets) => {
    return getfacetsWithUpdatedBrandValues(facets, {
      config: publicRuntimeConfig,
      appCtx,
      xlpName: pageName,
      xlpId,
    });
  };

  useEffect(() => {
    facetFilters(initialData.concat(availabilityFacets));
    setData(initialData);
  }, [initialData, availabilityFacets]);

  const changeURL = (rawUrl, hideMenuOnChange = true) => {
    let url = rawUrl;
    if (hideMenu.current && hideMenuOnChange) {
      hideMenu.current();
    }

    if (
      url.indexOf('layout=') === -1 &&
      appCtx.url &&
      appCtx.url.includes('layout=') &&
      appCtx.url.split('layout=')[1]
    ) {
      url = `${url}&layout=${appCtx.url.split('layout=')[1][0]}`;
    }

    if (pageName === 'category') {
      navigateWithUrlForCategory({
        site: tenant,
        url: isPLP && url.indexOf(isPLP) === -1 ? `${url}&isPLP=1` : url,
        onRouteChange: () => {
          setBusy(true);
        },
        categoryId: id,
        categoryName: name,
        store,
        regionCode,
        isWhiteLabelExperience,
      });
    } else if (pageName === 'collection') {
      navigateWithUrlForCollection({
        site: tenant,
        url,
        onRouteChange: () => {
          setBusy(true);
        },
        collectionId: id,
        store,
        regionCode,
        isWhiteLabelExperience,
      });
    } else if (pageName === 'seller') {
      navigateWithUrlForSeller({
        regionCode,
        site: tenant,
        url,
        onRouteChange: () => {
          setBusy(true);
        },
        sellerName: id,
        store,
        isWhiteLabelExperience,
      });
    } else if (pageName === 'brand') {
      navigateWithUrlForBrand({
        regionCode,
        site: tenant,
        url,
        onRouteChange: () => {
          setBusy(true);
        },
        brandName: id,
        store,
      });
    } else if (pageName === 'shop') {
      navigateWithUrlForShop({
        site: tenant,
        url,
        onRouteChange: () => {
          setBusy(true);
        },
        store,
        regionCode,
        shopId: id,
        isWhiteLabelExperience,
      });
    } else {
      navigateWithUrl({
        site: tenant,
        url,
        onRouteChange: () => {
          setBusy(true);
        },
        store,
        regionCode,
        isWhiteLabelExperience,
      });
    }
  };

  const selectedFacetsCount = useMemo(() => {
    const reducer = (acc, facet) => {
      let total = acc + facet.values.filter(({ selected }) => selected).length;
      if (facet.meta && facet.meta[0].selected) {
        total += 1;
      }
      return total;
    };
    const facetsCount = data.reduce(reducer, 0);
    const availabilityCount = availabilityFacets.reduce(reducer, 0);
    return facetsCount + availabilityCount;
  }, [data, availabilityFacets]);

  const renderMobileFacets = () => {
    // Find popularBrandFacet to pass to FacetsMobile
    const popularBrandFacet = data.find(
      (facet) =>
        constants.SWITCH_TOGGLE_REGEX?.test(facet.type) && facet?.name?.toLowerCase() === constants.POPULAR_BRAND
    );

    return (
      <>
        <FacetsMobile
          onHideMenu={(hideMenuFacets) => {
            hideMenu.current = hideMenuFacets;
          }}
          data={data.filter((d) => !constants.HIDDEN_DYNAMIC_FACET_MOBILE.test(d.type))}
          dataMethods={{
            type: 'availability',
            tenant,
            baseFacetsURL,
            changeURL,
          }}
          availabilityFacets={availabilityFacets}
          freeShippingFacet={freeShippingFacet}
          title={labels.FILTERS_TITLE_REBRANDING_MOBILE}
          sortOptions={sortOptions}
          dropDownLabel={dropDownLabel}
          selectedURLs={selectedURLs}
          pageName={pageName}
          onFacetChangeHandler={onFacetChange}
          getUpdatedFacets={getUpdatedFacets}
          appliedFacets={appliedFacets}
          pagination={pagination}
          handleScrollToTopProductList={handleScrollToTopProductList}
          isFiltersMenuVisible={isFiltersMenuVisible}
          setIsFiltersMenuVisible={setIsFiltersMenuVisible}
          canonicalUrl={canonicalUrl}
          popularBrandFacet={popularBrandFacet}
        />
        {(isDynamicFacetsEnabled || showShippingFacetsBar) && (
          <DynamicFacets
            availabilityFacets={availabilityFacets}
            facets={data}
            tenant={tenant}
            baseFacetsURL={baseFacetsURL}
            changeURL={changeURL}
            pageName={pageName}
            onFacetChange={onFacetChange}
            store={store}
            appCtx={appCtx}
            getUpdatedFacets={getUpdatedFacets}
            labels={labels}
            setIsFiltersMenuVisible={setIsFiltersMenuVisible}
            selectedFacetsCount={selectedFacetsCount}
          />
        )}
      </>
    );
  };
  const desktop = () => (
    <>
      {showFreeShippingSwitch && (
        <FacetsDesktop
          data={data.filter((d) => constants.FREE_SHIPPING_REGEX.test(d.name))}
          dataMethods={{
            type: 'facetsWithSwitch',
            tenant,
            baseFacetsURL,
            changeURL,
          }}
          pageName={pageName}
          type="facetsWithSwitch"
          onFacetChangeHandler={onFacetChange}
        />
      )}
      {isEventSwitchVisible && (
        <FacetsDesktop
          data={data.filter((d) => constants.EVENT_REGEX.test(d.type))}
          dataMethods={{
            type: 'facetsWithSwitch',
            tenant,
            baseFacetsURL,
            changeURL,
          }}
          pageName={pageName}
          type="facetsWithSwitch"
          onFacetChangeHandler={onFacetChange}
        />
      )}
      {(nonPksAvlFacets.length > 0 || freeShippingFacet) && (
        <FacetsDesktop
          data={availabilityFacets}
          freeShippingFacet={freeShippingFacet}
          dataMethods={{
            type: 'availability',
            tenant,
            baseFacetsURL,
            changeURL,
          }}
          pageName={pageName}
          type="availability"
          baseFacetsURL={baseFacetsURL}
          onFacetChangeHandler={onFacetChange}
        />
      )}
      <FacetsDesktop
        data={
          isFacetWithSwitchVisible
            ? data.filter((d) => {
                if (isEventSwitchVisible) {
                  return !constants.FREE_SHIPPING_REGEX.test(d.name) && !constants.EVENT_REGEX.test(d.type);
                }
                return !constants.FREE_SHIPPING_REGEX.test(d.name);
              })
            : data.filter((d) => {
                if (isEventSwitchVisible) {
                  return !constants.EVENT_REGEX.test(d.type);
                }
                return true;
              })
        }
        dataMethods={{
          type: '',
          tenant,
          baseFacetsURL,
          changeURL,
        }}
        pageName={pageName}
        type=""
        onFacetChangeHandler={onFacetChange}
        getUpdatedFacets={getUpdatedFacets}
        canonicalUrl={canonicalUrl}
      />
    </>
  );

  return (
    <MatchMedia
      content={{
        mobile: () => renderMobileFacets(),
        tablet: () => renderMobileFacets(),
        desktop,
      }}
    />
  );
};

Facets.defaultProps = {
  labels: {},
  appliedFacets: [],
  pageName: 'search',
  onFacetChange: noop,
  pagination: {},
  canonicalUrl: '',
};

Facets.propTypes = {
  baseFacetsURL: PropTypes.string.isRequired,
  appCtx: PropTypes.object.isRequired,
  sortOptions: PropTypes.array.isRequired,
  availabilityFacets: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  labels: PropTypes.object,
  pageName: PropTypes.string,
  onFacetChange: PropTypes.func,
  handleScrollToTopProductList: PropTypes.func.isRequired,
  appliedFacets: PropTypes.array,
  pagination: PropTypes.object,
  canonicalUrl: PropTypes.string,
};

export { Facets };
export default withApplicationContext(withLabels(Facets));
